import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Chat from './Chat';
import type { ChatLayout, ChatMessage } from './types';
import {
  agentApi,
  type AgentChatHistoryItem,
  type AgentChatScope,
  type AgentTextDelta,
} from '../../services/api';
import { useOptionalPatientTabs } from '../PatientTabs/PatientTabsContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { deriveChatScope, scopeKeyOf } from './chatScope';
import { requestChatPrefill } from './chatPrefill';
import {
  loadPersistedMessages,
  loadPersistedExpanded,
  persistMessages,
  persistExpanded,
  clearPersistedMessages,
  getOrCreateConversationKey,
  clearConversationKey,
  peekConversationKey,
  setConversationKey,
  threadFromStored,
  loadLayoutPrefs,
  persistLayoutPrefs,
  type LayoutPrefs,
} from './threadPersistence';
import {
  FLOATING_CHAT_BODY_CLASS,
  MOBILE_BREAKPOINT,
  PILL_BOTTOM,
  PILL_HEIGHT,
  RAIL_COLUMN_WIDTH,
  RAIL_RIGHT_OFFSET,
} from './floatingChatGeometry';

// One message as the agent's history sees it. PHA-1169: an assistant turn also
// carries its work log, so a follow-up ("rerun it for onboarded only") gets the
// exact query behind the earlier table, not just its prose.
function toHistoryItem(m: ChatMessage): AgentChatHistoryItem {
  const item: AgentChatHistoryItem = {
    role: m.role as 'user' | 'assistant' | 'system',
    content: m.content,
  };
  if (m.role === 'assistant' && m.workLog && m.workLog.length) item.work = m.workLog;
  return item;
}

// PHA-1155: other parts of the app (the /assistant route, a "Ask about this"
// button) open the workspace by dispatching this event.
export const OPEN_ASSISTANT_EVENT = 'phanova:open-assistant';
export type OpenAssistantDetail = { layout?: ChatLayout };

// Opens the workspace from anywhere. Writes the preference first so it also
// works when the chat has not mounted yet (a cold load of /assistant), then
// signals an already-mounted chat.
export function openAssistant(layout: ChatLayout = 'full'): void {
  persistLayoutPrefs({ ...loadLayoutPrefs(), mode: layout });
  persistExpanded(true);
  window.dispatchEvent(new CustomEvent<OpenAssistantDetail>(OPEN_ASSISTANT_EVENT, { detail: { layout } }));
}

// PHA-1168: "Ask Phanova" from another screen (a report's page): open the
// workspace and leave `text` (e.g. "#not-billable-this-month") in the input for
// the user to send. Keeps their layout unless the chat is the small panel,
// which is too cramped for a table, and then uses the side sheet.
export function askAssistant(text: string): void {
  const mode = loadLayoutPrefs().mode;
  requestChatPrefill(text);
  openAssistant(mode === 'panel' ? 'side' : mode);
}

const SIDE_MIN = 400;
const SIDE_MAX = 1100;

const FloatingChat: React.FC = () => {
  const [expanded, setExpanded] = useState(loadPersistedExpanded);
  const [panelMounted, setPanelMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // PHA-1155: workspace layout (compact panel / side sheet / full screen),
  // thread history and the open thread's key.
  const [layoutPrefs, setLayoutPrefs] = useState<LayoutPrefs>(loadLayoutPrefs);
  const layout = layoutPrefs.mode;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(() => peekConversationKey());
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [loadingThread, setLoadingThread] = useState(false);

  useEffect(() => {
    persistLayoutPrefs(layoutPrefs);
  }, [layoutPrefs]);

  const changeLayout = useCallback((mode: ChatLayout) => {
    setLayoutPrefs((p) => ({ ...p, mode }));
    setHistoryOpen(false);
  }, []);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<OpenAssistantDetail>).detail || {};
      if (detail.layout) changeLayout(detail.layout);
      setExpanded(true);
    };
    window.addEventListener(OPEN_ASSISTANT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, onOpen);
  }, [changeLayout]);

  // PHA-549: derive the same scope SmartHub uses (active patient tab, or
  // clinic-wide on the patient list). Sent with every /api/agent/chat call so
  // the backend can inject a focus + briefing block into the system prompt.
  const tabsCtx = useOptionalPatientTabs();
  const { orgId, orgName } = useOrganization();
  const chatScope: AgentChatScope | null = useMemo(
    () => deriveChatScope(tabsCtx, orgId, orgName),
    [orgId, orgName, tabsCtx?.tabs, tabsCtx?.activeTabId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const scopeKey = scopeKeyOf(chatScope);

  // Chat transcript lives up here so it survives the Chat component being
  // unmounted when the panel collapses. sessionStorage hydration ensures it
  // also survives a same-tab reload — the user can close the widget, refresh
  // the dashboard, reopen, and pick up where they left off.
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadPersistedMessages());
  const [isProcessing, setIsProcessing] = useState(false);
  const idCounter = useRef(1000);
  const nextId = useCallback(() => String(++idCounter.current), []);

  useEffect(() => {
    persistMessages(messages);
  }, [messages]);

  // PHA-549: when the user switches patient tabs (or jumps back to the patient
  // list) mid-conversation, drop a small system notice into the thread so both
  // sides have a visible cue that the agent's focus has shifted. The new
  // turn's system prompt already carries the new # Current focus block; this
  // notice exists purely for UI legibility.
  const prevScopeKeyRef = useRef(scopeKey);
  useEffect(() => {
    if (prevScopeKeyRef.current === scopeKey) return;
    prevScopeKeyRef.current = scopeKey;
    setMessages((curr) => {
      if (curr.length === 0) return curr;
      const label = !chatScope
        ? 'Scope cleared'
        : chatScope.kind === 'patient'
          ? `Now viewing ${chatScope.patientName || chatScope.patientId}`
          : `Now viewing patient list${chatScope.orgName ? ` · ${chatScope.orgName}` : ''}`;
      const noticeMsg: ChatMessage = {
        id: `sys-${Date.now()}-${nextId()}`,
        role: 'system',
        content: label,
        timestamp: new Date().toISOString(),
      };
      return [...curr, noticeMsg];
    });
    // chatScope intentionally excluded from deps — scopeKey covers identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Resolve a placeholder assistant message with either a successful agent
  // result (with pendingApproval preserved if present) or an error message.
  // PHA-1155: answers whose text already arrived live. Tracked outside state:
  // the final envelope can land before the buffered delta frame is applied
  // (always, on a re-attach replay), and must still not replay the typewriter.
  const streamedIdsRef = useRef<Set<string>>(new Set());

  const applyAgentResult = useCallback(
    (placeholderId: string, result: Awaited<ReturnType<typeof agentApi.chat>>) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderId
            ? {
                ...m,
                content: result.reply,
                parts: result.parts,
                question: result.question,
                workLog: result.workLog,
                toolUses: result.toolUses,
                pendingApproval: result.pendingApproval
                  ? { ...result.pendingApproval }
                  : undefined,
                // PHA-1155: when the reply already streamed in live, show it
                // as-is; replaying it through the typewriter would make the
                // clinician watch the same text type out twice.
                isStreaming: !(m.liveText || streamedIdsRef.current.has(placeholderId)),
                liveText: undefined,
                liveStreamId: undefined,
                streamComplete: true,
                agent: result.agent,
                promptVersion: result.promptVersion,
                model: result.model,
                // Only overwrite when the server sent traceEvents (non-stream
                // back-compat path). In the streaming path the client has
                // already accumulated them live via onEvent.
                traceEvents:
                  result.traceEvents !== undefined ? result.traceEvents : m.traceEvents,
              }
            : m
        )
      );
    },
    []
  );

  const applyAgentError = useCallback((placeholderId: string, err: unknown) => {
    const data = (err as any)?.errorData;
    const detail =
      (data && (data.details || data.error)) ||
      (err instanceof Error && err.message) ||
      'Unknown error';
    setMessages((prev) =>
      prev.map((m) =>
        m.id === placeholderId
          ? {
              ...m,
              content: `Sorry — the Phanova agent failed: ${detail}`,
              isStreaming: false,
              liveText: undefined,
              liveStreamId: undefined,
              streamComplete: true,
            }
          : m
      )
    );
  }, []);

  // PHA-1155: live answer text. Deltas arrive a word or so at a time, so they
  // are buffered and applied once per animation frame rather than re-rendering
  // the whole thread on every fragment. A new streamId (the next model turn)
  // replaces the draft instead of appending to it.
  const makeDeltaAppender = useCallback((placeholderId: string) => {
    let pending: AgentTextDelta[] = [];
    let scheduled = false;
    const flush = () => {
      scheduled = false;
      const batch = pending;
      pending = [];
      if (batch.length === 0) return;
      setMessages((prev) =>
        prev.map((m) => {
          // A frame buffered just before the final envelope must not bring the
          // draft back over the finished answer.
          if (m.id !== placeholderId || m.streamComplete) return m;
          let liveText = m.liveText || '';
          let liveStreamId = m.liveStreamId;
          for (const d of batch) {
            if (d.streamId !== liveStreamId) {
              liveText = '';
              liveStreamId = d.streamId;
            }
            liveText += d.text;
          }
          return { ...m, liveText, liveStreamId };
        })
      );
    };
    return (delta: AgentTextDelta) => {
      streamedIdsRef.current.add(placeholderId);
      pending.push(delta);
      if (scheduled) return;
      scheduled = true;
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(flush);
      } else {
        setTimeout(flush, 16);
      }
    };
  }, []);

  const makeEventAppender = useCallback(
    (placeholderId: string) => (event: any) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderId ? { ...m, traceEvents: [...(m.traceEvents || []), event] } : m
        )
      );
    },
    []
  );

  // PHA-1155: remember which server-side run is producing this answer.
  const makeRunIdSetter = useCallback(
    (placeholderId: string) => (runId: string) => {
      setMessages((prev) => prev.map((m) => (m.id === placeholderId ? { ...m, runId } : m)));
    },
    []
  );

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isProcessing) return;

      // Snapshot prior history BEFORE adding the new user turn so the backend
      // sees the same context the user sees. System notices ("Now viewing X")
      // are included so the LLM has continuity when the user switches scope
      // mid-conversation — otherwise a follow-up like "and her last visit?"
      // loses its referent.
      const priorHistory: AgentChatHistoryItem[] = messages
        .filter(
          (m) => m.role === 'user' || m.role === 'assistant' || m.role === 'system',
        )
        .map(toHistoryItem);

      const userMsg: ChatMessage = {
        id: nextId(),
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      };

      const placeholderId = nextId();
      const placeholder: ChatMessage = {
        id: placeholderId,
        role: 'assistant',
        content: 'Thinking…',
        timestamp: new Date().toISOString(),
        // isStreaming controls the reply-text typewriter. The ThinkingPanel
        // computes its own streaming state from traceEvents (open while the
        // last event isn't turn_end), so we leave isStreaming false until the
        // reply text arrives.
        isStreaming: false,
        toolUses: [],
        traceEvents: [],
      };

      setMessages((prev) => [...prev, userMsg, placeholder]);
      setIsProcessing(true);
      setActiveKey(getOrCreateConversationKey());

      const appendEvent = makeEventAppender(placeholderId);

      try {
        const result = await agentApi.chat(
          trimmed,
          priorHistory,
          appendEvent,
          undefined,
          chatScope ?? undefined,
          getOrCreateConversationKey(),
          makeDeltaAppender(placeholderId),
          makeRunIdSetter(placeholderId),
        );
        applyAgentResult(placeholderId, result);
      } catch (err) {
        applyAgentError(placeholderId, err);
      } finally {
        setIsProcessing(false);
        setHistoryRefresh((n) => n + 1);
      }
    },
    [messages, isProcessing, nextId, applyAgentResult, applyAgentError, chatScope, makeDeltaAppender, makeEventAppender, makeRunIdSetter]
  );

  // User clicked Approve or Deny on a pending action. Mark the old message's
  // approval as resolved, show a new thinking placeholder, then POST the
  // decision to /api/agent/chat with { resumeApproval } and append whatever
  // comes back as the next assistant message (could be a final answer or
  // another pending approval).
  const handleDecideApproval = useCallback(
    async (approvalId: string, approved: boolean) => {
      if (isProcessing) return;

      // Mark the source approval card as resolved so the Approve/Deny
      // buttons disappear and the card turns green/red.
      setMessages((prev) =>
        prev.map((m) =>
          m.pendingApproval && m.pendingApproval.id === approvalId
            ? {
                ...m,
                pendingApproval: {
                  ...m.pendingApproval,
                  resolved: approved ? 'approved' : 'denied',
                },
                toolUses: (m.toolUses || []).map((t, i, arr) =>
                  i === arr.length - 1
                    ? { ...t, resolved: approved ? 'approved' : 'denied' }
                    : t
                ),
              }
            : m
        )
      );

      const placeholderId = nextId();
      const placeholder: ChatMessage = {
        id: placeholderId,
        role: 'assistant',
        content: approved ? 'Executing approved action…' : 'Cancelling action…',
        timestamp: new Date().toISOString(),
        isStreaming: false,
        toolUses: [],
        traceEvents: [],
      };
      setMessages((prev) => [...prev, placeholder]);
      setIsProcessing(true);

      const appendEvent = makeEventAppender(placeholderId);

      try {
        // PHA-1071: send the transcript up to (not including) the bubble we
        // just added, so the outcome is stored on its own turn directly after
        // the proposal it resolves.
        const resumeHistory: AgentChatHistoryItem[] = messages
          .filter(
            (m) => m.role === 'user' || m.role === 'assistant' || m.role === 'system',
          )
          .map(toHistoryItem);
        const result = await agentApi.resume(
          approvalId,
          approved,
          undefined,
          appendEvent,
          undefined,
          getOrCreateConversationKey(),
          resumeHistory,
          makeDeltaAppender(placeholderId),
          makeRunIdSetter(placeholderId),
        );
        applyAgentResult(placeholderId, result);
      } catch (err) {
        applyAgentError(placeholderId, err);
      } finally {
        setIsProcessing(false);
        setHistoryRefresh((n) => n + 1);
      }
    },
    [isProcessing, nextId, applyAgentResult, applyAgentError, messages, makeDeltaAppender, makeEventAppender, makeRunIdSetter]
  );

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setIsProcessing(false);
    clearPersistedMessages();
    // PHA-1071: a new chat is a new server-side thread. Without this the next
    // turn would be appended to the previous conversation's transcript.
    clearConversationKey();
    setActiveKey(null);
    setHistoryOpen(false);
  }, []);

  // PHA-1155: a reload (or a crash of the tab) no longer loses an answer that
  // was still being worked on: the run keeps going server-side, and on mount we
  // re-attach to it and replay it from the start.
  const attachedRef = useRef(false);
  useEffect(() => {
    if (attachedRef.current) return;
    attachedRef.current = true;
    const pending = [...messages]
      .reverse()
      .find((m) => m.role === 'assistant' && m.runId && !m.streamComplete);
    if (!pending || !pending.runId) return;
    const placeholderId = pending.id;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === placeholderId
          ? { ...m, content: 'Reconnecting…', traceEvents: [], liveText: undefined, liveStreamId: undefined }
          : m
      )
    );
    setIsProcessing(true);
    agentApi
      .attachRun(pending.runId, {
        onEvent: makeEventAppender(placeholderId),
        onDelta: makeDeltaAppender(placeholderId),
      })
      .then((result) => applyAgentResult(placeholderId, result))
      .catch((err) => {
        if ((err as any)?.status === 404) {
          applyAgentError(placeholderId, {
            errorData: {
              details:
                'this answer was interrupted before it finished (the request expired or the server restarted). Please ask again.',
            },
          });
        } else {
          applyAgentError(placeholderId, err);
        }
      })
      .finally(() => {
        setIsProcessing(false);
        setHistoryRefresh((n) => n + 1);
      });
    // Runs once, on mount, against the transcript restored from storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PHA-1155: Stop. Takes effect between model turns; the run then finishes
  // with a short "stopped" answer through the normal final envelope.
  const handleStop = useCallback(() => {
    const pending = [...messages]
      .reverse()
      .find((m) => m.role === 'assistant' && m.runId && !m.streamComplete);
    if (pending?.runId) {
      agentApi.cancelRun(pending.runId).catch(() => {
        /* the run may already have finished */
      });
    }
  }, [messages]);

  // PHA-1155: reopen a past thread and keep writing to it. Positions are
  // realigned with the stored turn indexes (threadFromStored) so a new turn
  // appends rather than overwriting an old one.
  const handleSelectThread = useCallback(
    async (conversationKey: string) => {
      if (isProcessing) return;
      if (conversationKey === activeKey && messages.length > 0) {
        setHistoryOpen(false);
        return;
      }
      setLoadingThread(true);
      try {
        const res = await agentApi.getConversation(conversationKey);
        const thread = threadFromStored(res.messages || [], `h${Date.now()}`);
        setMessages(thread);
        setConversationKey(conversationKey);
        setActiveKey(conversationKey);
        setHistoryOpen(false);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            id: `sys-${Date.now()}`,
            role: 'system',
            content: "Couldn't open that conversation. Try again in a moment.",
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        setLoadingThread(false);
      }
    },
    [isProcessing, activeKey, messages.length]
  );

  // Side-sheet resize: drag the left edge.
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const onMove = (ev: MouseEvent) => {
      const max = Math.min(SIDE_MAX, window.innerWidth - 200);
      const width = Math.min(Math.max(window.innerWidth - ev.clientX, SIDE_MIN), Math.max(SIDE_MIN, max));
      setLayoutPrefs((p) => ({ ...p, sideWidth: Math.round(width) }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mount Chat when expanding, unmount after collapse animation finishes.
  useEffect(() => {
    if (expanded) {
      setPanelMounted(true);
      return;
    }
    const t = window.setTimeout(() => setPanelMounted(false), 320);
    return () => window.clearTimeout(t);
  }, [expanded]);

  // An in-progress conversation is "sticky": it stays open while the clinician
  // clicks around the app, navigates between screens and works in the chart,
  // and only closes when they explicitly close it or start a new chat. Before
  // PHA-1071 any outside click collapsed the panel, which made the agent
  // unusable for anything that required looking at the page underneath it.
  const threadActive = messages.length > 0 || isProcessing;

  // Remember the open/closed state across route changes and same-tab reloads.
  useEffect(() => {
    persistExpanded(expanded);
  }, [expanded]);

  // Click outside to collapse — only while the thread is empty.
  useEffect(() => {
    if (!expanded || threadActive || layout !== 'panel') return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [expanded, threadActive, layout]);

  // Escape to collapse. Never interrupts a request in flight.
  useEffect(() => {
    if (!expanded) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isProcessing) setExpanded(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expanded, isProcessing]);

  // Reserve room at the bottom of every page so the closed pill never sits
  // over its last rows.
  useEffect(() => {
    document.body.classList.add(FLOATING_CHAT_BODY_CLASS);
    return () => document.body.classList.remove(FLOATING_CHAT_BODY_CLASS);
  }, []);

  // Width matches the Smart Hub column above (see floatingChatGeometry).
  const railColumnWidth = RAIL_COLUMN_WIDTH;
  const collapsedWidth = isMobile
    ? 'calc(100vw - 32px)'
    : railColumnWidth;
  const collapsedHeight = PILL_HEIGHT;

  // Expanded panel keeps the same horizontal footprint as the rail column;
  // it just grows taller upward.
  const expandedWidth = isMobile ? '100vw' : railColumnWidth;
  const expandedHeight = isMobile
    ? '100dvh'
    : 'min(720px, calc(100vh - 48px))';

  const railRightOffset = RAIL_RIGHT_OFFSET;

  // PHA-1155: side sheet docks to the right edge at full height; full screen
  // covers the viewport. Mobile is always full screen when open.
  const docked = expanded && !isMobile && layout !== 'panel';
  const edgeToEdge = expanded && (isMobile || docked);
  const dockedWidth = layout === 'full' ? '100vw' : `min(${layoutPrefs.sideWidth}px, 100vw)`;

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: edgeToEdge ? 0 : PILL_BOTTOM,
    right: edgeToEdge ? 0 : railRightOffset,
    zIndex: 9999,
    width: docked ? dockedWidth : expanded ? expandedWidth : collapsedWidth,
    height: docked ? '100vh' : expanded ? expandedHeight : collapsedHeight,
    backgroundColor: '#fff',
    borderRadius: edgeToEdge ? 0 : expanded ? 16 : 26,
    boxShadow: expanded
      ? '0 24px 60px rgba(15, 23, 42, 0.18), 0 4px 16px rgba(15, 23, 42, 0.08)'
      : '0 6px 20px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
    transition:
      'width 320ms cubic-bezier(0.2, 0.9, 0.3, 1), ' +
      'height 320ms cubic-bezier(0.2, 0.9, 0.3, 1), ' +
      'border-radius 320ms ease, ' +
      'box-shadow 320ms ease, ' +
      'bottom 320ms ease, ' +
      'right 320ms ease',
  };

  return (
    <div ref={containerRef} style={containerStyle}>
      {/* Collapsed pill UI */}
      <CollapsedPill
        visible={!expanded}
        onOpen={() => setExpanded(true)}
        hasHistory={messages.length > 0}
      />

      {/* Expanded chat panel */}
      {panelMounted && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: expanded ? 1 : 0,
            transition: 'opacity 180ms ease',
            transitionDelay: expanded ? '140ms' : '0ms',
            pointerEvents: expanded ? 'auto' : 'none',
          }}
        >
          <Chat
            messages={messages}
            isProcessing={isProcessing}
            onSend={handleSend}
            onNewChat={handleNewChat}
            onDecideApproval={handleDecideApproval}
            onStop={handleStop}
            onClose={() => setExpanded(false)}
            compact
            scope={chatScope}
            layout={isMobile ? 'panel' : layout}
            onChangeLayout={isMobile ? undefined : changeLayout}
            historyOpen={historyOpen}
            onToggleHistory={() => setHistoryOpen((v) => !v)}
            activeConversationKey={activeKey}
            onSelectThread={handleSelectThread}
            historyRefreshToken={historyRefresh}
            loadingThread={loadingThread}
          />
        </div>
      )}

      {docked && layout === 'side' && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          onMouseDown={startResize}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: 'ew-resize',
            zIndex: 3,
          }}
        />
      )}
    </div>
  );
};

const CollapsedPill: React.FC<{
  visible: boolean;
  onOpen: () => void;
  hasHistory: boolean;
}> = ({ visible, onOpen, hasHistory }) => (
  <button
    type="button"
    onClick={onOpen}
    aria-label="Ask Phanova AI"
    style={{
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      padding: '0 8px 0 22px',
      border: 'none',
      background: 'transparent',
      cursor: 'text',
      fontSize: 15,
      color: '#94a3b8',
      fontFamily: 'inherit',
      textAlign: 'left',
      opacity: visible ? 1 : 0,
      transition: 'opacity 140ms ease',
      pointerEvents: visible ? 'auto' : 'none',
    }}
  >
    <span>{hasHistory ? 'Continue your chat' : 'Ask Phanova AI'}</span>
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 38,
        height: 38,
        borderRadius: '50%',
        background:
          'radial-gradient(circle at 30% 30%, #d1fae5 0%, #a7f3d0 60%, #6ee7b7 100%)',
        flexShrink: 0,
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="9" cy="10.5" r="1.2" fill="#047857" />
        <circle cx="15" cy="10.5" r="1.2" fill="#047857" />
        <path
          d="M9 14.5c0.9 1.2 1.9 1.7 3 1.7s2.1-0.5 3-1.7"
          stroke="#047857"
          strokeWidth="1.4"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </span>
  </button>
);

export default FloatingChat;
